import { FC, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { getData, post, put } from 'services/api';
import { queryClient } from 'index';
import { typedYupResolver } from 'features/common/utils/typedYupResolver';
import { postToast } from 'features/common/utils/postFlash';
// Shared single-source validation — the same schema the accordion PoolForm uses, so both pool
// surfaces enforce one rule set (TIC-255: inline + standalone share the same data model).
import { buildPoolSchema } from 'features/common/utils/poolSchema';
import { getPoolMemberLock, getPoolSoldFloor, PoolMemberTicketType } from 'features/common/utils/poolGuards';

import { Form } from 'features/common/components/Form';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupInput } from 'features/common/components/Punchup/primitives/forms/fields/PunchupInput';
import { PunchupNumericInput } from 'features/common/components/Punchup/primitives/forms/fields/PunchupNumericInput';
import { PunchupTextArea } from 'features/common/components/Punchup/primitives/forms/fields/PunchupTextArea';
import { PunchupCheckbox } from 'features/common/components/Punchup/primitives/forms/fields/PunchupCheckbox';
import { PunchupFieldLabel } from 'features/common/components/Punchup/primitives/forms/fields/PunchupFieldLabel';
import { PunchupPageHeader } from 'features/common/components/Punchup/primitives/layout/PunchupPageHeader';
import { PunchupPageShell } from 'features/common/components/Punchup/pages/PunchupPageShell';
import { useSeededEditForm } from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';

import styles from './PunchupPoolDetails.module.css';

interface PoolFormValues {
	name: string;
	description?: string;
	total_amount: number;
	ticket_type_ids: string[];
}

interface PoolDetailsApi extends PoolFormValues {
	ID: number;
	event_id: number;
	ticket_types?: { ID: number; name: string }[];
}

/**
 * PunchupPoolDetails
 *
 * Standalone Punchup sub-page for creating and editing a pool — a non-seated allocation bucket:
 * a named capacity (`total_amount`) shared across one or more ticket types. Reached by direct URL
 * only; inside the iframe, pools are managed inline via the `PoolsSection` accordion (`PoolForm`).
 * This page is the standalone twin of `PoolForm` and shares its field primitives, validation
 * schema, and seed-once lifecycle so the two surfaces stay in lockstep. `poolId` absent → create
 * (POST); present → edit (PUT). On success, navigates back to the dashboard.
 */
const PunchupPoolDetails: FC = () => {
	const { t } = useTranslation();
	const { id: eventId, poolId } = useParams<{ id: string; poolId?: string }>();
	const history = useHistory();
	const isNewPool = !poolId;

	// Read at validation time (not capture time) so the Lifetime-Σ floor stays current as the
	// member selection settles — see docs/pools_business_logic.md T-3.
	const soldFloorRef = useRef(0);
	const schema = useMemo(() => buildPoolSchema(t, () => soldFloorRef.current), [t]);

	const formMethods = useForm<PoolFormValues>({
		resolver: typedYupResolver(schema),
		defaultValues: { name: '', description: '', total_amount: 0, ticket_type_ids: [] },
	});
	const {
		watch,
		setValue,
		formState: { isSubmitting, errors },
	} = formMethods;

	const selectedTicketTypeIds = (watch('ticket_type_ids') ?? []) as string[];

	const eventTicketTypesQuery = useQuery(
		['event-ticket-types', eventId],
		async () => {
			const data = await getData(`events/${eventId}`);
			return data.ticket_types || [];
		},
		{ enabled: !!eventId }
	);
	const eventTicketTypes = (eventTicketTypesQuery.data ?? []) as PoolMemberTicketType[];
	// A pool can never be smaller than the tickets its members have already sold (T-3).
	const soldFloor = getPoolSoldFloor(selectedTicketTypeIds, eventTicketTypes);
	soldFloorRef.current = soldFloor;

	// Fetch the existing pool on edit and seed the form once behind a spinner (no blink; a later
	// refetch can't clobber in-progress edits) — the same lifecycle as the accordion PoolForm.
	const { data: pool, isError: isPoolError } = useQuery<PoolDetailsApi>({
		queryKey: ['pool', eventId, poolId],
		queryFn: () => getData(`/events/${eventId}/pools/${poolId}`),
		enabled: !isNewPool && !!poolId,
	});

	const { isSeeded, seedGate } = useSeededEditForm<PoolDetailsApi, PoolFormValues>({
		isEditMode: !isNewPool,
		formMethods,
		seedData: pool,
		toFormValues: (data) => ({
			name: data.name,
			description: data.description || '',
			total_amount: data.total_amount,
			ticket_type_ids: data.ticket_types ? data.ticket_types.map((tt) => tt.ID.toString()) : [],
		}),
		isError: isPoolError,
	});

	const handleCheckboxChange = (id: string) => {
		const current = (formMethods.getValues('ticket_type_ids') ?? []) as string[];
		const newIds = current.includes(id) ? current.filter((tid) => tid !== id) : [...current, id];
		setValue('ticket_type_ids', newIds, { shouldDirty: true, shouldValidate: true });
	};

	const onCancel = () => history.push(`/home/dashboard/${eventId}`);

	const onSubmit = async (data: PoolFormValues) => {
		const payload = {
			...data,
			total_amount: Number(data.total_amount),
			ticket_type_ids: data.ticket_type_ids || [],
		};
		try {
			if (isNewPool && eventId) {
				await post(`/events/${eventId}/pools`, payload);
			} else if (poolId) {
				await put(`/events/${eventId}/pools/${poolId}`, payload);
			}
			queryClient.invalidateQueries({ queryKey: ['eventPools', eventId] });
			history.push(`/home/dashboard/${eventId}`);
		} catch {
			// A backend rejection (e.g. total below sold) must keep the user's values and not navigate.
			postToast('error', t('pool-save-failed', 'Failed to save pool'));
		}
	};

	const pageName = isNewPool ? 'Add Pool' : 'Edit Pool';

	// Edit mode is held behind the spinner until the saved pool is seeded (create renders immediately).
	if (!isSeeded) {
		return (
			<PunchupPageShell pageName={pageName} onBack={onCancel}>
				{seedGate}
			</PunchupPageShell>
		);
	}

	return (
		<PunchupPageShell pageName={pageName} onBack={onCancel}>
			<div className={styles.container}>
				<PunchupPageHeader
					title={isNewPool ? t('poolDetails-add-title', 'Add Pool') : t('poolDetails-edit-title', 'Edit Pool')}
					description={
						isNewPool
							? t('poolDetails-add-description', 'Group ticket types together under a shared capacity.')
							: t('poolDetails-edit-description', 'Update the capacity and ticket types in this pool.')
					}
				/>
				<Form formMethods={formMethods} onSubmit={onSubmit}>
					<div className={styles.formFields}>
						<PunchupInput
							id="name"
							name="name"
							label={t('poolDetails-name-label')}
							placeholder={t('poolDetails-name-placeholder')}
							required
						/>

						<PunchupTextArea
							id="description"
							name="description"
							label={t('poolDetails-description-label')}
							placeholder={t('poolDetails-description-placeholder')}
						/>

						<PunchupNumericInput
							id="total_amount"
							name="total_amount"
							unit="count"
							showStepper
							label={t('poolDetails-total-label')}
							placeholder={t('poolDetails-total-placeholder')}
							required
							help={
								t(
									'poolDetails-total-help',
									"The total number of tickets this pool can sell, shared across all the ticket types you attach to it. It can't be set below what's already sold."
								) +
								(soldFloor > 0
									? ' ' +
									  t(
											'poolDetails-total-floor-hint',
											"{{count}} tickets are already sold, so the pool can't go lower.",
											{
												count: soldFloor,
											}
									  )
									: '')
							}
							min={soldFloor}
						/>

						<div>
							<PunchupFieldLabel
								as="span"
								label={t('poolDetails-ticketTypes-label')}
								help={t(
									'poolDetails-ticketTypes-help',
									'Pick the ticket types that draw from this shared capacity. A ticket type can belong to only one pool at a time.'
								)}
							/>
							<div className={styles.checkboxList}>
								{eventTicketTypes.map((tt) => {
									const isSelected = selectedTicketTypeIds.includes(tt.ID.toString());
									const lock = getPoolMemberLock(tt, isSelected, poolId ?? null);
									const tooltip =
										lock === 'attached-elsewhere'
											? t('poolDetails-ticketType-attached-tooltip')
											: lock === 'selling-locked'
											? t('poolDetails-ticketType-selling-locked-tooltip')
											: lock === 'selling-add'
											? t('poolDetails-ticketType-selling-add-tooltip')
											: undefined;
									return (
										<PunchupCheckbox
											key={tt.ID}
											checked={isSelected}
											onChange={() => handleCheckboxChange(tt.ID.toString())}
											disabled={lock !== null}
											label={tt.name}
											title={tooltip}
										/>
									);
								})}
							</div>
							{errors.ticket_type_ids?.message && (
								<span className="punchupFormError">{errors.ticket_type_ids.message as string}</span>
							)}
						</div>
					</div>

					<div className={styles.formActions}>
						<PunchupButton type="button" variant="secondary" text={t('button-cancel')} onClick={onCancel} />
						<PunchupButton
							type="submit"
							variant="primary"
							disabled={isSubmitting}
							text={isNewPool ? t('button-create-new') : t('button-save')}
						/>
					</div>
				</Form>
			</div>
		</PunchupPageShell>
	);
};

export { PunchupPoolDetails };
